// ============================================================
// ProductionGuidance.js — Production tips and mixing guidance
//
// Provides actionable, section-specific production advice based
// on genre, energy level, and arrangement context. This is the
// "producer mentor" layer that turns structural knowledge into
// practical tips the user can execute in their DAW.
// ============================================================

/**
 * Get production guidance for a specific section within an arrangement.
 *
 * @param {Object} section - Section from generated arrangement
 * @param {Object} context - Analysis context
 * @param {string} context.genre - Genre name
 * @param {string} context.family - Genre family (electronic, hip-hop, etc.)
 * @param {number} context.bpm - Track BPM
 * @param {string} context.key - Track key
 * @param {string} context.loopRole - Role of the user's loop
 * @param {string[]} context.loopElements - Elements in the user's loop
 * @returns {{
 *   mixTips: string[],
 *   transitionTip: string,
 *   dawActions: string[],
 *   energyTip: string,
 *   referenceTracks: string[]
 * }}
 */
export function getSectionGuidance(section, context) {
  const { genre, family, bpm, key, loopRole, loopElements } = context;

  return {
    mixTips: getMixTips(section, context),
    transitionTip: getTransitionTip(section, context),
    dawActions: getDAWActions(section, context),
    energyTip: getEnergyTip(section, context),
    referenceTracks: getReferenceTracks(genre, section.type)
  };
}

// --- Mix Tips by section type and genre ---

function getMixTips(section, { genre, family, bpm }) {
  const tips = [];
  const energy = section.energy;
  const type = section.type;

  // Universal tips based on energy level (techniques from established audio engineering practice)
  if (energy < 0.3) {
    tips.push('Keep the mix sparse. Let each element breathe with space and reverb. [Arrangement contrast principle]');
    tips.push('Use longer reverb tails here — they\'ll get masked once the energy builds. [Pre-masking technique]');
  } else if (energy >= 0.8) {
    tips.push('Tighten the low end. Sidechain the bass to the kick for clarity at high energy. [Ducking technique — French house production]');
    tips.push('Keep reverb tails shorter here to maintain punch and definition. [Haas effect management]');
  }

  // Genre-specific mix tips (grounded in established production methodology)
  if (family === 'electronic') {
    if (type === 'drop' || type === 'first-drop' || type === 'second-drop') {
      tips.push('The drop should feel physically louder than the build. Use subtle limiting or saturation on the master bus. [Perceived loudness — Fletcher-Munson curves]');
      tips.push('Make sure the kick and bass are in mono below 150Hz. [Mono-compatibility principle — essential for club systems]');
      if (genre.includes('Bass') || genre.includes('DnB') || genre === 'Drum & Bass') {
        tips.push('Layer your sub bass with a mid-bass element for speaker translation. Sub alone disappears on small speakers. [Harmonic layering technique]');
      }
    }
    if (type === 'breakdown') {
      tips.push('This is your widest stereo moment. Pan elements hard left/right for contrast with the drop\'s centered energy. [Mid/side contrast technique]');
      tips.push('Automate a high-pass filter on the master — cutting below 80Hz makes the drop hit harder when the low end returns. [Subtractive arrangement — tension through absence]');
    }
    if (type === 'buildup') {
      tips.push('Automate a low-pass filter opening over the section. Start filtered, end wide open right before the drop. [Filter sweep — classic trance/house build technique]');
      tips.push('Add a riser or white noise sweep that builds for the full section length. [Psychoacoustic anticipation cue]');
      tips.push('Gradually increase the snare/clap roll frequency: quarter notes → eighths → sixteenths. [Rhythmic density escalation]');
    }
  }

  if (family === 'hip-hop') {
    if (type === 'verse') {
      tips.push('Leave 3-6dB of headroom in the vocal frequency range (300Hz-3kHz) for the rapper/vocalist. [Frequency slotting — Bob Katz gain staging principles]');
      tips.push('Keep the 808 pattern steady but not overcrowded. Space between hits lets the bass breathe. [Negative space in rhythm design]');
    }
    if (type === 'chorus') {
      tips.push('Double the main melody or add harmonies to widen the chorus. [ADT — automatic double tracking, pioneered at Abbey Road]');
      tips.push('Boost the overall loudness slightly — the hook should feel bigger than the verse. [Section-level dynamics — arrangement loudness mapping]');
    }
  }

  if (family === 'rock') {
    if (type === 'verse') {
      tips.push('Pan rhythm guitars hard left/right (80-100%). Keep the center clear for vocals and bass. [LCR panning philosophy]');
      tips.push('Use a tighter, less distorted guitar tone in verses to create headroom for the chorus. [Dynamic range preservation across sections]');
    }
    if (type === 'chorus') {
      tips.push('Layer a second guitar take panned opposite to thicken the wall of sound. [Double-tracking — standard rock production technique]');
      tips.push('Open up the cymbals — ride and crash cymbals add energy and width. [Frequency spectrum filling — cymbal wash as energy indicator]');
    }
  }

  return tips.slice(0, 4); // Cap at 4 tips
}

// --- Transition Tips ---

function getTransitionTip(section, { genre, family, bpm }) {
  const type = section.type;

  // Transition INTO this section (based on established arrangement craft)
  const transitions = {
    'intro': 'Start clean — a single element fading in or an impact on beat 1. [First impression principle — hook within 8 bars]',
    'buildup': 'Use a fill or drum roll from the previous section. Cut the bass briefly before to create anticipation. [Tension/release — subtractive arrangement]',
    'drop': 'Hit beat 1 HARD. Use a crash, impact sample, or momentary silence (1/8 note gap) right before. Contrast between build and drop is everything. [The "moment of silence" technique]',
    'first-drop': 'Hit beat 1 HARD. Crash cymbal, impact sample, or momentary silence right before. [Energy contrast principle]',
    'second-drop': 'Second drop can hit even harder. Try a longer silence (1/4 note) or a reverse cymbal. [Escalation principle — raise the stakes]',
    'breakdown': 'Pull elements out gradually — remove the kick first, then the bass over 2-4 bars. Let it breathe. [Element subtraction — Noisia production approach]',
    'break': 'Quick cut. Pull bass and kick simultaneously on beat 1. A reverse reverb tail can smooth it. [Hard transition technique]',
    'verse': 'Drop into the groove naturally. Pull back energy by removing layers rather than fading. [Arrangement dynamics — verse as foundation]',
    'chorus': 'Lift the energy on beat 1. A fill in the last bar of the verse sets up the chorus. Every element enters together. [The "payoff" principle — Max Martin arrangement logic]',
    'pre-chorus': 'Start building. Add one new element at the start to signal the change. [Harmonic anticipation — dominant function]',
    'bridge': 'Surprise the listener. Different drums, half-time feel, or even a moment of silence. [Contrast as arrangement device]',
    'outro': 'Begin removing elements from the final section. The last 4 bars should start transitioning. [Mirror intro principle — bookend structure]',
  };

  // Genre-specific transition overrides
  if (family === 'electronic' && type === 'drop') {
    if (bpm >= 165) {
      return 'D&B drop: Create a 1-beat silence right before beat 1, then slam in with kick + bass + full drums simultaneously. The contrast is what makes D&B drops hit.';
    }
    if (bpm >= 124 && bpm <= 132) {
      return 'House drop: Let the kick lead. Start the drop with just kick + bass for 1-2 bars before bringing in the full pattern. Four-on-the-floor should lock in immediately.';
    }
  }

  return transitions[type] || 'Transition smoothly — use a drum fill or reversed element to bridge into this section.';
}

// --- DAW Actions (specific things to do in Ableton/DAW) ---

function getDAWActions(section, { genre, family, bpm, key, loopElements }) {
  const actions = [];
  const type = section.type;
  const energy = section.energy;

  // Universal actions
  if (section.isLoopSection) {
    actions.push('Place your loop here — this is where it goes in the arrangement.');
  }

  if (type === 'buildup') {
    actions.push('Create an automation lane on your main synth/bass — automate the filter cutoff from low to high over the full section.');
    actions.push('Add a white noise riser: use a simple noise oscillator with a rising pitch envelope.');
    actions.push('If using Ableton: create a snare roll with Note Repeat (1/4 → 1/8 → 1/16 over the section).');
  }

  if (type === 'breakdown') {
    actions.push('Duplicate your loop but mute the kick and bass tracks. Let the melodic/atmospheric elements play alone.');
    actions.push('Add a generous reverb send (2-4 second tail) to the remaining elements for spaciousness.');
    if (family === 'electronic') {
      actions.push('Automate a high-pass filter on the master bus: sweep from 20Hz up to 200Hz over the breakdown to thin out the low end.');
    }
  }

  if (type === 'intro') {
    actions.push('Start with a single element from your loop — isolate just one track (atmosphere, hi-hat, or a filtered version of the main element).');
    actions.push('Consider adding a long, evolving reverb or delay tail to create movement in the sparse mix.');
  }

  if (type === 'outro') {
    actions.push('Mirror your intro in reverse — remove elements in the opposite order you introduced them.');
    actions.push('Add a final crash cymbal or downlifter on the last beat for a clean ending.');
  }

  if (type === 'drop' || type === 'chorus') {
    if (!section.isLoopSection) {
      actions.push('Variation of your main loop: try changing the bass pattern, swapping a drum sound, or adding a new top-line melody.');
    }
    if (loopElements.includes('bass') || loopElements.includes('sub-bass')) {
      actions.push('Check your kick-bass relationship: sidechain compress the bass from the kick (4:1 ratio, fast attack, medium release).');
    }
  }

  // Key-specific guidance
  if (key && type === 'breakdown') {
    actions.push(`Your track is in ${key}. For the breakdown, try using the relative major/minor chords for emotional contrast.`);
  }

  return actions.slice(0, 5); // Cap at 5 actions
}

// --- Energy Tips ---

function getEnergyTip(section, { family }) {
  const energy = section.energy;

  if (energy >= 0.9) {
    return 'Maximum energy. Every element at full level, tight compression, and maximum presence. This should be the loudest section.';
  }
  if (energy >= 0.7) {
    return 'High energy. Most elements present but leave a small amount of headroom — the peak sections need to feel even bigger.';
  }
  if (energy >= 0.5) {
    return 'Medium energy. Core elements driving the groove but with space. Think of this as "building toward something."';
  }
  if (energy >= 0.3) {
    return 'Low-medium energy. Stripped back to essentials. Wide reverbs and delays fill the space where elements used to be.';
  }
  return 'Minimal energy. Atmosphere only. One or two elements creating mood. Silence and space are your instruments here.';
}

// --- Reference Track Suggestions ---

const REFERENCE_TRACKS = {
  'Drum & Bass': {
    'drop': ['Sub Focus — "Rock It"', 'Chase & Status — "Blind Faith"', 'Netsky — "Memory Lane"'],
    'breakdown': ['Calibre — "Even If"', 'London Elektricity — "Just One Second"'],
    'intro': ['Andy C — "Heartbeat Loud"', 'High Contrast — "If We Ever"'],
    'default': ['Sub Focus — "Tidal Wave"', 'Wilkinson — "Afterglow"'],
  },
  'Bass House': {
    'drop': ['Jauz — "Feel the Volume"', 'AC Slater — "Bass Inside"', 'JOYRYDE — "Hot Drum"'],
    'breakdown': ['Chris Lake — "Turning of the Tide"'],
    'default': ['Chris Lake — "I Want You"', 'Fisher — "Losing It"'],
  },
  'Techno': {
    'drop': ['Amelie Lens — "Hypnotized"', 'Charlotte de Witte — "Sage"'],
    'breakdown': ['Tale of Us — "Endless"'],
    'default': ['Adam Beyer — "Your Mind"', 'Enrico Sangiuliano — "Symbiosis"'],
  },
  'Trap': {
    'chorus': ['RL Grime — "Core"', 'Baauer — "Temple"'],
    'verse': ['Metro Boomin — "Superhero"'],
    'default': ['Flume — "Never Be Like You"', 'TNGHT — "Higher Ground"'],
  },
  'Pop': {
    'chorus': ['Max Martin productions', 'Billie Eilish — "Bad Guy" (arrangement reference)'],
    'verse': ['Dua Lipa — "Levitating" (verse dynamics)'],
    'default': ['The Weeknd — "Blinding Lights" (energy curve reference)'],
  },
  'Rock': {
    'chorus': ['Foo Fighters — "Everlong"', 'Green Day — "Basket Case"'],
    'bridge': ['Radiohead — "Creep" (bridge dynamics)'],
    'default': ['Queens of the Stone Age — "No One Knows"'],
  },
};

function getReferenceTracks(genre, sectionType) {
  const genreRefs = REFERENCE_TRACKS[genre] || REFERENCE_TRACKS['Pop'] || {};
  return genreRefs[sectionType] || genreRefs['default'] || ['Study arrangements in your genre for section-specific inspiration.'];
}

/**
 * Generate a complete production guide for an entire arrangement.
 * Returns guidance for every section in the arrangement.
 *
 * @param {Object} arrangement - Result from generateArrangement()
 * @param {Object} analysisContext - Context from analysis pipeline
 * @returns {Array<{sectionLabel: string, guidance: Object}>}
 */
export function generateFullGuidance(arrangement, analysisContext) {
  const context = {
    genre: arrangement.genre,
    family: getFamily(arrangement.genre),
    bpm: arrangement.bpm,
    key: arrangement.key,
    loopRole: arrangement.loopRole,
    loopElements: analysisContext.loopElements || []
  };

  return arrangement.sections.map(section => ({
    sectionLabel: section.label,
    sectionType: section.type,
    bars: section.bars,
    startBar: section.startBar,
    energy: section.energy,
    isLoopSection: section.isLoopSection,
    guidance: getSectionGuidance(section, context)
  }));
}

function getFamily(genre) {
  const familyMap = {
    'Drum & Bass': 'electronic', 'Liquid DnB': 'electronic', 'Neurofunk': 'electronic',
    'Bass House': 'electronic', 'Deep House': 'electronic', 'Tech House': 'electronic',
    'Techno': 'electronic', 'Melodic Techno': 'electronic', 'Trance': 'electronic',
    'Dubstep': 'electronic', 'Hardstyle': 'electronic', 'Jungle': 'electronic',
    'Boom Bap': 'hip-hop', 'Trap': 'hip-hop', 'Lo-Fi Hip Hop': 'hip-hop', 'Drill': 'hip-hop',
    'Pop': 'pop', 'R&B': 'pop',
    'Rock': 'rock', 'Punk Rock': 'rock', 'Metal': 'rock', 'Melodic Hardcore': 'rock',
  };
  return familyMap[genre] || 'electronic';
}
